import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api';
import { useApp } from '../context/AppContext';
import { t } from '../i18n/translations';

const TYPE_LABELS = {
  doctor_visit: '🩺 Doctor Visit',
  blood_test: '🩸 Blood Test',
  dentist: '🦷 Dentist',
  specialist: '👨‍⚕️ Specialist',
  other: '📋 Other'
};

const TYPE_COLORS = {
  doctor_visit: { bg: '#dbeafe', color: '#1d4ed8' },
  blood_test: { bg: '#fee2e2', color: '#dc2626' },
  dentist: { bg: '#fef9c3', color: '#b45309' },
  specialist: { bg: '#f3e8ff', color: '#7c3aed' },
  other: { bg: '#f1f5f9', color: '#475569' }
};

const MOODS = [
  { value: 'great', label: '😄 Great', color: '#16a34a', bg: '#dcfce7' },
  { value: 'good', label: '🙂 Good', color: '#2563eb', bg: '#dbeafe' },
  { value: 'okay', label: '😐 Okay', color: '#b45309', bg: '#fef9c3' },
  { value: 'poor', label: '😟 Poor', color: '#ea580c', bg: '#ffedd5' },
  { value: 'bad', label: '😢 Bad', color: '#dc2626', bg: '#fee2e2' }
];

const VITAL_TYPES = [
  { value: 'blood_pressure', label: '🩸 Blood Pressure', unit: 'mmHg', color: '#dc2626' },
  { value: 'heart_rate', label: '❤️ Heart Rate', unit: 'bpm', color: '#ef4444' },
  { value: 'weight', label: '⚖️ Weight', unit: 'kg', color: '#2563eb' },
  { value: 'blood_glucose', label: '🍬 Blood Glucose', unit: 'mg/dL', color: '#b45309' }
];

const s = {
  page: { maxWidth: 920, margin: '0 auto', padding: '32px 16px' },
  back: { color: 'var(--primary)', cursor: 'pointer', marginBottom: 20, fontSize: 15, fontWeight: 600 },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  profileCard: { background: 'var(--surface)', borderRadius: 16, padding: 28, marginBottom: 28, boxShadow: 'var(--shadow)', display: 'flex', alignItems: 'center', gap: 24 },
  avatar: { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--border)', flexShrink: 0 },
  avatarInitials: { width: 80, height: 80, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: '#fff', flexShrink: 0 },
  name: { fontSize: 24, fontWeight: 700, color: 'var(--heading)', marginBottom: 4 },
  info: { color: 'var(--text3)', fontSize: 14, marginBottom: 3 },
  cgBadge: { display: 'inline-block', background: 'var(--success-bg)', color: 'var(--success-text)', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, marginTop: 6 },
  pdfBtn: { background: '#7c3aed', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  emailBtn: { background: '#0891b2', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btn: { background: '#16a34a', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  tabs: { display: 'flex', gap: 4, marginBottom: 28, background: 'var(--surface2)', borderRadius: 12, padding: 5 },
  tab: { flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, transition: 'all 0.15s' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: 'var(--heading)' },
  // Appointment cards
  card: { background: 'var(--surface)', borderRadius: 14, padding: 20, marginBottom: 12, boxShadow: 'var(--shadow)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  cardLeft: { flex: 1 },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--heading)', marginBottom: 4 },
  cardSub: { color: 'var(--text3)', fontSize: 13, marginBottom: 3 },
  typeBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, marginRight: 6 },
  statusBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  cardBtns: { display: 'flex', gap: 8, flexShrink: 0 },
  doneBtn: { background: 'var(--success-bg)', color: 'var(--success-text)', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  editBtn: { background: 'var(--surface2)', color: 'var(--text3)', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  delBtn: { background: 'var(--error-bg)', color: 'var(--error-text)', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  resultsBtn: { background: 'var(--primary-light)', color: 'var(--primary)', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  resultsBox: { background: 'var(--success-bg)', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginTop: 8, fontSize: 13, color: 'var(--success-text)' },
  // Vitals
  vitalTypeRow: { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' },
  vitalTypeBtn: { padding: '8px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  vitalCard: { background: 'var(--surface)', borderRadius: 14, padding: 18, marginBottom: 10, boxShadow: 'var(--shadow)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  vitalVal: { fontSize: 22, fontWeight: 800, color: 'var(--heading)' },
  vitalUnit: { fontSize: 13, color: 'var(--text3)', marginLeft: 4 },
  vitalDate: { color: 'var(--text4)', fontSize: 13 },
  chartWrap: { background: 'var(--surface)', borderRadius: 14, padding: 20, marginBottom: 20, boxShadow: 'var(--shadow)' },
  // Journal
  moodRow: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  moodBtn: { padding: '8px 16px', borderRadius: 20, border: '2px solid transparent', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  journalCard: { background: 'var(--surface)', borderRadius: 14, padding: 20, marginBottom: 12, boxShadow: 'var(--shadow)' },
  journalDate: { fontSize: 13, fontWeight: 700, color: 'var(--text3)', marginBottom: 8 },
  journalText: { color: 'var(--text2)', fontSize: 14, marginBottom: 6 },
  journalMeta: { color: 'var(--text4)', fontSize: 13 },
  // Modal
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 },
  modalCard: { background: 'var(--surface)', padding: 36, borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: 20, fontWeight: 700, marginBottom: 22, color: 'var(--heading)' },
  label: { display: 'block', marginBottom: 6, fontWeight: 600, color: 'var(--text2)', fontSize: 14 },
  input: { width: '100%', padding: '11px 13px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, marginBottom: 14, boxSizing: 'border-box', background: 'var(--surface2)', color: 'var(--text)' },
  row: { display: 'flex', gap: 12, marginTop: 8 },
  cancelBtn: { flex: 1, padding: 12, border: '1.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 600 },
  saveBtn: { flex: 1, padding: 12, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  empty: { textAlign: 'center', padding: 40, color: 'var(--text4)', background: 'var(--surface2)', borderRadius: 12 },
  errorMsg: { color: 'var(--error-text)', fontSize: 14, marginBottom: 12 }
};

const emptyApptForm = { title: '', type: 'doctor_visit', date: '', time: '09:00', location: '', notes: '' };
const emptyVitalForm = { type: 'blood_pressure', value: '', systolic: '', diastolic: '', date: new Date().toISOString().split('T')[0], time: `${String(new Date().getHours()).padStart(2,'0')}:${String(new Date().getMinutes()).padStart(2,'0')}`, notes: '' };
const emptyJournalForm = { mood: 'good', notes: '', activity: '', appetite: '', date: new Date().toISOString().split('T')[0] };

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function getAge(dob) {
  if (!dob) return '';
  return Math.floor((Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
}

function formatDate(date) {
  if (!date) return '';
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
}

export default function CaregiverDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useApp();
  const T = (key) => t(language, key);
  const [caregiver, setCaregiver] = useState(null);
  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  const [activeTab, setActiveTab] = useState('appointments');
  // Appointments
  const [appointments, setAppointments] = useState([]);
  const [showApptModal, setShowApptModal] = useState(false);
  const [editAppt, setEditAppt] = useState(null);
  const [apptForm, setApptForm] = useState(emptyApptForm);
  const [apptError, setApptError] = useState('');
  const [showResultsModal, setShowResultsModal] = useState(null);
  const [resultsText, setResultsText] = useState('');
  // Vitals
  const [vitals, setVitals] = useState([]);
  const [vitalType, setVitalType] = useState('blood_pressure');
  const [showVitalModal, setShowVitalModal] = useState(false);
  const [vitalForm, setVitalForm] = useState(emptyVitalForm);
  // Journal
  const [journal, setJournal] = useState([]);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [editJournal, setEditJournal] = useState(null);
  const [journalForm, setJournalForm] = useState(emptyJournalForm);
  // SMS Invite
  const [smsInviteSending, setSmsInviteSending] = useState(false);
  const [smsInviteMsg, setSmsInviteMsg] = useState(null);

  useEffect(() => { fetchAll(); }, [id]);

  const fetchAll = async () => {
    const [caregiverRes, apptRes, vitalsRes, journalRes] = await Promise.all([
      api.get('/caregivers'),
      api.get('/appointments'),
      api.get(`/vitals/senior/${id}`),   // reuses same endpoint with caregiver id
      api.get(`/journal/senior/${id}`)    // reuses same endpoint with caregiver id
    ]);
    setCaregiver(caregiverRes.data.find(c => c._id === id));
    setAppointments(apptRes.data.filter(a => a.personId === id));
    setVitals(vitalsRes.data);
    setJournal(journalRes.data);
  };

  // ---- APPOINTMENTS ----
  const openAddAppt = () => { setEditAppt(null); setApptForm(emptyApptForm); setApptError(''); setShowApptModal(true); };
  const openEditAppt = (appt) => {
    setEditAppt(appt);
    setApptForm({ title: appt.title, type: appt.type, date: appt.date, time: appt.time, location: appt.location || '', notes: appt.notes || '' });
    setApptError(''); setShowApptModal(true);
  };
  const closeApptModal = () => { setShowApptModal(false); setEditAppt(null); setApptForm(emptyApptForm); setApptError(''); };
  const submitAppt = async (e) => {
    e.preventDefault(); setApptError('');
    try {
      if (editAppt) await api.put(`/appointments/${editAppt._id}`, { ...apptForm, personType: 'caregiver', personId: id, personName: caregiver?.name });
      else await api.post('/appointments', { ...apptForm, personType: 'caregiver', personId: id, personName: caregiver?.name });
      closeApptModal(); fetchAll();
    } catch (err) { setApptError(err.response?.data?.error || 'Something went wrong'); }
  };
  const markComplete = async (apptId) => { await api.put(`/appointments/${apptId}/complete`); fetchAll(); };
  const deleteAppt = async (apptId) => { if (!window.confirm('Delete this appointment?')) return; await api.delete(`/appointments/${apptId}`); fetchAll(); };
  const openResults = (appt) => { setShowResultsModal(appt); setResultsText(appt.results || ''); };
  const saveResults = async () => { await api.put(`/appointments/${showResultsModal._id}/results`, { results: resultsText }); setShowResultsModal(null); fetchAll(); };

  const statusStyle = (status) => {
    if (status === 'completed') return { bg: '#dcfce7', color: '#16a34a' };
    if (status === 'missed') return { bg: '#fee2e2', color: '#dc2626' };
    return { bg: '#dbeafe', color: '#1d4ed8' };
  };

  // ---- VITALS ----
  const openAddVital = () => { setVitalForm({ ...emptyVitalForm, type: vitalType }); setShowVitalModal(true); };
  const closeVitalModal = () => { setShowVitalModal(false); setVitalForm(emptyVitalForm); };
  const submitVital = async (e) => {
    e.preventDefault();
    await api.post('/vitals', { ...vitalForm, seniorId: id }); // uses caregiver id as person id
    closeVitalModal(); fetchAll();
  };
  const deleteVital = async (vid) => { await api.delete(`/vitals/${vid}`); fetchAll(); };

  const filteredVitals = vitals.filter(v => v.type === vitalType);
  const chartData = filteredVitals.slice(-20).map(v => ({ date: v.date, value: v.value || null, systolic: v.systolic || null, diastolic: v.diastolic || null }));
  const currentVitalType = VITAL_TYPES.find(t => t.value === vitalType);
  const formatVitalDisplay = (v) => v.type === 'blood_pressure' ? `${v.systolic}/${v.diastolic}` : v.value;

  // ---- JOURNAL ----
  const openAddJournal = () => { setEditJournal(null); setJournalForm(emptyJournalForm); setShowJournalModal(true); };
  const openEditJournal = (entry) => {
    setEditJournal(entry);
    setJournalForm({ mood: entry.mood, notes: entry.notes || '', activity: entry.activity || '', appetite: entry.appetite || '', date: entry.date });
    setShowJournalModal(true);
  };
  const closeJournalModal = () => { setShowJournalModal(false); setEditJournal(null); setJournalForm(emptyJournalForm); };
  const submitJournal = async (e) => {
    e.preventDefault();
    if (editJournal) await api.put(`/journal/${editJournal._id}`, journalForm);
    else await api.post('/journal', { ...journalForm, seniorId: id }); // uses caregiver id
    closeJournalModal(); fetchAll();
  };
  const deleteJournal = async (jid) => { if (!window.confirm('Delete this journal entry?')) return; await api.delete(`/journal/${jid}`); fetchAll(); };

  // ---- SMS INVITE ----
  const sendSmsInvite = async () => {
    setSmsInviteSending(true); setSmsInviteMsg(null);
    try {
      const res = await api.post(`/caregivers/${id}/send-sms-invite`);
      setSmsInviteMsg({ type: 'success', text: res.data.message });
      fetchAll();
    } catch (err) {
      setSmsInviteMsg({ type: 'error', text: err.response?.data?.error || 'Failed to send invite' });
    } finally { setSmsInviteSending(false); }
  };

  // ---- PDF ----
  const generatePDF = () => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    doc.setFillColor(22, 163, 74);
    doc.rect(0, 0, 210, 32, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20); doc.setFont(undefined, 'bold');
    doc.text('SeniorCare — Caregiver Report', 14, 14);
    doc.setFontSize(11); doc.setFont(undefined, 'normal');
    doc.text(`Generated: ${today}`, 14, 24);

    doc.setTextColor(30, 58, 138);
    doc.setFontSize(16); doc.setFont(undefined, 'bold');
    doc.text(caregiver?.name || '', 14, 46);
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10); doc.setFont(undefined, 'normal');
    let yPos = 54;
    if (caregiver?.relationship) { doc.text(`Relationship: ${caregiver.relationship}`, 14, yPos); yPos += 7; }
    if (caregiver?.date_of_birth) { doc.text(`Age: ${getAge(caregiver.date_of_birth)} years`, 14, yPos); yPos += 7; }
    if (caregiver?.phone) { doc.text(`Phone: ${caregiver.phone}`, 14, yPos); yPos += 7; }
    yPos += 6;

    // Appointments
    doc.setTextColor(30, 58, 138); doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text('Upcoming Appointments', 14, yPos); yPos += 4;
    const upcomingAppts = appointments.filter(a => a.status === 'upcoming');
    if (upcomingAppts.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Title', 'Type', 'Date', 'Time', 'Location']],
        body: upcomingAppts.map(a => [a.title, a.type.replace('_', ' '), a.date, a.time, a.location || '-']),
        headStyles: { fillColor: [22, 163, 74] },
        styles: { fontSize: 9 }, margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    } else { doc.setTextColor(148, 163, 184); doc.setFontSize(10); doc.text('No upcoming appointments.', 14, yPos + 4); yPos += 14; }

    // Vitals
    doc.setTextColor(30, 58, 138); doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text('Recent Vitals (last 10)', 14, yPos); yPos += 4;
    const recentVitals = vitals.slice(0, 10);
    if (recentVitals.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Type', 'Value', 'Date', 'Notes']],
        body: recentVitals.map(v => [
          VITAL_TYPES.find(t => t.value === v.type)?.label.replace(/[^\w\s]/g, '').trim() || v.type,
          v.type === 'blood_pressure' ? `${v.systolic}/${v.diastolic} mmHg` : `${v.value} ${v.unit || ''}`,
          v.date, v.notes || '-'
        ]),
        headStyles: { fillColor: [124, 58, 237] },
        styles: { fontSize: 9 }, margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    } else { doc.setTextColor(148, 163, 184); doc.setFontSize(10); doc.text('No vitals recorded.', 14, yPos + 4); yPos += 14; }

    // Journal
    doc.setTextColor(30, 58, 138); doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text('Recent Journal Entries (last 10)', 14, yPos); yPos += 4;
    const recentJournal = journal.slice(0, 10);
    if (recentJournal.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Mood', 'Notes', 'Activity', 'Appetite']],
        body: recentJournal.map(j => [j.date, j.mood, j.notes || '-', j.activity || '-', j.appetite || '-']),
        headStyles: { fillColor: [37, 99, 235] },
        styles: { fontSize: 9 }, margin: { left: 14, right: 14 }
      });
    } else { doc.setTextColor(148, 163, 184); doc.setFontSize(10); doc.text('No journal entries.', 14, yPos + 4); }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setTextColor(148, 163, 184); doc.setFontSize(9);
      doc.text(`SeniorCare Caregiver Report — ${caregiver?.name} — Page ${i} of ${pageCount}`, 14, 290);
    }

    doc.save(`${(caregiver?.name || 'Caregiver').replace(/\s+/g, '_')}_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const sendEmailReport = async () => {
    if (!emailTo) return;
    setEmailSending(true); setEmailMsg(null);
    try {
      const doc = new jsPDF();
      doc.setFillColor(22, 163, 74);
      doc.rect(0, 0, 210, 32, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18); doc.setFont(undefined, 'bold');
      doc.text('SeniorCare — Caregiver Report', 14, 14);
      doc.setFontSize(10); doc.setFont(undefined, 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 24);
      doc.setTextColor(30, 58, 138); doc.setFontSize(15); doc.setFont(undefined, 'bold');
      doc.text(caregiver?.name || '', 14, 46);
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      await api.post('/reports/email', { to: emailTo, subject: `Caregiver Report — ${caregiver?.name}`, pdfBase64, fileName: `${(caregiver?.name || 'Caregiver').replace(/\s+/g, '_')}_report.pdf` });
      setEmailMsg({ type: 'success', text: `Report sent to ${emailTo}!` });
    } catch (err) {
      setEmailMsg({ type: 'error', text: err.response?.data?.error || 'Failed to send email' });
    } finally { setEmailSending(false); }
  };

  if (!caregiver) return <div style={s.page}><div style={s.back} onClick={() => navigate('/')}>← Back</div><div>Loading...</div></div>;

  return (
    <div style={s.page} className="page-wrap">
      <div style={s.back} onClick={() => navigate('/')}>← Back to Dashboard</div>

      {/* Profile Card + PDF button */}
      <div style={s.topBar}>
        <div style={s.profileCard} className="surface">
          {caregiver.photo
            ? <img src={`${caregiver.photo}`} alt={caregiver.name} style={s.avatar} />
            : <div style={s.avatarInitials}>{getInitials(caregiver.name)}</div>
          }
          <div>
            <div style={s.name}>{caregiver.name}</div>
            {caregiver.relationship && <div style={s.info}>Relationship: {caregiver.relationship}</div>}
            {caregiver.date_of_birth && <div style={s.info}>Age: {getAge(caregiver.date_of_birth)} years old</div>}
            {caregiver.phone && <div style={s.info}>📞 {caregiver.phone}</div>}
            {caregiver.notes && <div style={s.info}>📝 {caregiver.notes}</div>}
            <span style={s.cgBadge}>Caregiver</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} className="btn-group">
          <button style={s.pdfBtn} onClick={generatePDF}>📄 {T('pdfReport')}</button>
          <button style={s.emailBtn} onClick={() => { setShowEmailModal(true); setEmailMsg(null); }}>✉️ {T('emailReport')}</button>
        </div>
      </div>

      {/* SMS Opt-In Status */}
      {caregiver.phone && (
        <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '14px 20px', marginBottom: 20, boxShadow: 'var(--shadow)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>📱</span>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--heading)', fontSize: 14 }}>SMS Alerts</div>
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                {caregiver.smsOptIn === true
                  ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Opted in — SOS & appointment alerts active</span>
                  : caregiver.smsOptIn === false
                    ? <span style={{ color: '#dc2626', fontWeight: 600 }}>Opted out</span>
                    : <span style={{ color: '#b45309', fontWeight: 600 }}>Not enrolled — invite not sent yet</span>}
              </div>
            </div>
          </div>
          {caregiver.smsOptIn !== true && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {smsInviteMsg && (
                <span style={{ fontSize: 13, color: smsInviteMsg.type === 'success' ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                  {smsInviteMsg.text}
                </span>
              )}
              <button
                style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: smsInviteSending ? 'not-allowed' : 'pointer', opacity: smsInviteSending ? 0.7 : 1 }}
                onClick={sendSmsInvite}
                disabled={smsInviteSending}
              >
                {smsInviteSending ? 'Sending...' : '📨 Send SMS Invite'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabs} className="tab-bar">
        {[['appointments',`📅 ${T('appointments')}`],['vitals',`📊 ${T('vitals')}`],['journal',`📝 ${T('journal')}`]].map(([key, label]) => (
          <button key={key} style={{ ...s.tab, background: activeTab === key ? 'var(--surface)' : 'transparent', color: activeTab === key ? 'var(--heading)' : 'var(--text3)', boxShadow: activeTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}
            onClick={() => setActiveTab(key)}>{label}</button>
        ))}
      </div>

      {/* ===== APPOINTMENTS TAB ===== */}
      {activeTab === 'appointments' && (
        <>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Appointments ({appointments.length})</div>
            <button style={s.btn} onClick={openAddAppt}>+ Add Appointment</button>
          </div>
          {appointments.length === 0 ? (
            <div style={s.empty}><div style={{ fontSize: 40, marginBottom: 10 }}>📅</div><div style={{ fontWeight: 600, marginBottom: 4 }}>No appointments yet</div><div style={{ fontSize: 14 }}>Click "+ Add Appointment" to schedule one</div></div>
          ) : appointments.map(appt => {
            const typeStyle = TYPE_COLORS[appt.type] || TYPE_COLORS.other;
            const sSt = statusStyle(appt.status);
            return (
              <div key={appt._id} style={s.card}>
                <div style={s.cardLeft}>
                  <div style={s.cardTitle}>{appt.title}</div>
                  <div style={{ marginBottom: 6 }}>
                    <span style={{ ...s.typeBadge, background: typeStyle.bg, color: typeStyle.color }}>{TYPE_LABELS[appt.type] || appt.type}</span>
                    <span style={{ ...s.statusBadge, background: sSt.bg, color: sSt.color }}>{appt.status ? appt.status.charAt(0).toUpperCase() + appt.status.slice(1) : 'Upcoming'}</span>
                  </div>
                  <div style={s.cardSub}>📅 {formatDate(appt.date)} at {appt.time}</div>
                  {appt.location && <div style={s.cardSub}>📍 {appt.location}</div>}
                  {appt.notes && <div style={{ ...s.cardSub, fontStyle: 'italic' }}>{appt.notes}</div>}
                  {appt.results && <div style={s.resultsBox}>📋 Results: {appt.results}</div>}
                </div>
                <div style={s.cardBtns}>
                  {appt.status === 'upcoming' && <button style={s.doneBtn} onClick={() => markComplete(appt._id)}>Done</button>}
                  {appt.status === 'completed' && <button style={s.resultsBtn} onClick={() => openResults(appt)}>{appt.results ? 'Edit Results' : 'Add Results'}</button>}
                  <button style={s.editBtn} onClick={() => openEditAppt(appt)}>Edit</button>
                  <button style={s.delBtn} onClick={() => deleteAppt(appt._id)}>Delete</button>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ===== VITALS TAB ===== */}
      {activeTab === 'vitals' && (
        <>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Health Vitals</div>
            <button style={s.btn} onClick={openAddVital}>+ Log Vital</button>
          </div>
          <div style={s.vitalTypeRow}>
            {VITAL_TYPES.map(t => (
              <button key={t.value} style={{ ...s.vitalTypeBtn, background: vitalType === t.value ? t.color : '#f1f5f9', color: vitalType === t.value ? '#fff' : '#475569' }}
                onClick={() => setVitalType(t.value)}>{t.label}</button>
            ))}
          </div>
          {filteredVitals.length === 0 ? (
            <div style={s.empty}><div style={{ fontSize: 44, marginBottom: 12 }}>📊</div><div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No {currentVitalType?.label} readings yet</div><div>Click "+ Log Vital" to add the first reading</div></div>
          ) : (
            <>
              {chartData.length > 1 && (
                <div style={s.chartWrap}>
                  <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: 14 }}>{currentVitalType?.label} — Last {chartData.length} readings</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      {vitalType === 'blood_pressure' ? (
                        <>
                          <Line type="monotone" dataKey="systolic" stroke="#dc2626" strokeWidth={2} dot={{ r: 4 }} name="Systolic" />
                          <Line type="monotone" dataKey="diastolic" stroke="#f87171" strokeWidth={2} dot={{ r: 4 }} name="Diastolic" />
                        </>
                      ) : (
                        <Line type="monotone" dataKey="value" stroke={currentVitalType?.color} strokeWidth={2} dot={{ r: 4 }} name={currentVitalType?.label} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              {filteredVitals.map(v => (
                <div key={v._id} style={s.vitalCard}>
                  <div>
                    <span style={s.vitalVal}>{formatVitalDisplay(v)}</span>
                    <span style={s.vitalUnit}>{v.type === 'blood_pressure' ? 'mmHg' : v.unit}</span>
                    {v.notes && <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{v.notes}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={s.vitalDate}>{v.date} {v.time}</div>
                    <button style={{ ...s.delBtn, marginTop: 8, padding: '5px 10px', fontSize: 12 }} onClick={() => deleteVital(v._id)}>Delete</button>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {/* ===== JOURNAL TAB ===== */}
      {activeTab === 'journal' && (
        <>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Daily Journal</div>
            <button style={s.btn} onClick={openAddJournal}>+ Add Entry</button>
          </div>
          {journal.length === 0 ? (
            <div style={s.empty}><div style={{ fontSize: 44, marginBottom: 12 }}>📝</div><div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No journal entries yet</div><div>Record daily observations, mood and activity</div></div>
          ) : journal.map(entry => {
            const mood = MOODS.find(m => m.value === entry.mood) || MOODS[1];
            return (
              <div key={entry._id} style={s.journalCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={s.journalDate}>{new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    <span style={{ background: mood.bg, color: mood.color, padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>{mood.label}</span>
                    {entry.notes && <div style={{ ...s.journalText, marginTop: 10 }}>{entry.notes}</div>}
                    <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                      {entry.activity && <div style={s.journalMeta}>🏃 Activity: {entry.activity}</div>}
                      {entry.appetite && <div style={s.journalMeta}>🍽️ Appetite: {entry.appetite}</div>}
                    </div>
                  </div>
                  <div style={s.cardBtns}>
                    <button style={s.editBtn} onClick={() => openEditJournal(entry)}>Edit</button>
                    <button style={s.delBtn} onClick={() => deleteJournal(entry._id)}>Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ===== APPOINTMENT MODAL ===== */}
      {showApptModal && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>{editAppt ? 'Edit Appointment' : 'Add Appointment'}</div>
            <form onSubmit={submitAppt}>
              {apptError && <div style={s.errorMsg}>{apptError}</div>}
              <label style={s.label}>Title *</label>
              <input style={s.input} value={apptForm.title} onChange={e => setApptForm({ ...apptForm, title: e.target.value })} required placeholder="e.g. Annual checkup" />
              <label style={s.label}>Type</label>
              <select style={s.input} value={apptForm.type} onChange={e => setApptForm({ ...apptForm, type: e.target.value })}>
                <option value="doctor_visit">Doctor Visit</option>
                <option value="blood_test">Blood Test</option>
                <option value="dentist">Dentist</option>
                <option value="specialist">Specialist</option>
                <option value="other">Other</option>
              </select>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Date *</label>
                  <input style={s.input} type="date" value={apptForm.date} onChange={e => setApptForm({ ...apptForm, date: e.target.value })} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Time *</label>
                  <input style={s.input} type="time" value={apptForm.time} onChange={e => setApptForm({ ...apptForm, time: e.target.value })} required />
                </div>
              </div>
              <label style={s.label}>Location</label>
              <input style={s.input} value={apptForm.location} onChange={e => setApptForm({ ...apptForm, location: e.target.value })} placeholder="e.g. City Hospital" />
              <label style={s.label}>Notes</label>
              <input style={s.input} value={apptForm.notes} onChange={e => setApptForm({ ...apptForm, notes: e.target.value })} placeholder="Any notes..." />
              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeApptModal}>Cancel</button>
                <button type="submit" style={s.saveBtn}>{editAppt ? 'Update' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== VITAL MODAL ===== */}
      {showVitalModal && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>Log Vital Reading</div>
            <form onSubmit={submitVital}>
              <label style={s.label}>Type</label>
              <select style={s.input} value={vitalForm.type} onChange={e => setVitalForm({ ...vitalForm, type: e.target.value })}>
                {VITAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {vitalForm.type === 'blood_pressure' ? (
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Systolic (upper) *</label>
                    <input style={s.input} type="number" value={vitalForm.systolic} onChange={e => setVitalForm({ ...vitalForm, systolic: e.target.value })} required placeholder="e.g. 120" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Diastolic (lower) *</label>
                    <input style={s.input} type="number" value={vitalForm.diastolic} onChange={e => setVitalForm({ ...vitalForm, diastolic: e.target.value })} required placeholder="e.g. 80" />
                  </div>
                </div>
              ) : (
                <>
                  <label style={s.label}>Value * ({currentVitalType?.unit})</label>
                  <input style={s.input} type="number" step="0.1" value={vitalForm.value} onChange={e => setVitalForm({ ...vitalForm, value: e.target.value })} required placeholder={`Enter ${currentVitalType?.label}`} />
                </>
              )}
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Date *</label>
                  <input style={s.input} type="date" value={vitalForm.date} onChange={e => setVitalForm({ ...vitalForm, date: e.target.value })} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Time</label>
                  <input style={s.input} type="time" value={vitalForm.time} onChange={e => setVitalForm({ ...vitalForm, time: e.target.value })} />
                </div>
              </div>
              <label style={s.label}>Notes</label>
              <input style={s.input} value={vitalForm.notes} onChange={e => setVitalForm({ ...vitalForm, notes: e.target.value })} placeholder="e.g. After morning walk" />
              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeVitalModal}>Cancel</button>
                <button type="submit" style={s.saveBtn}>Save Reading</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== JOURNAL MODAL ===== */}
      {showJournalModal && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>{editJournal ? 'Edit Journal Entry' : 'Add Journal Entry'}</div>
            <form onSubmit={submitJournal}>
              <label style={s.label}>Date *</label>
              <input style={s.input} type="date" value={journalForm.date} onChange={e => setJournalForm({ ...journalForm, date: e.target.value })} required />
              <label style={s.label}>Mood</label>
              <div style={s.moodRow}>
                {MOODS.map(m => (
                  <button type="button" key={m.value}
                    style={{ ...s.moodBtn, background: journalForm.mood === m.value ? m.bg : '#f8fafc', color: journalForm.mood === m.value ? m.color : '#64748b', borderColor: journalForm.mood === m.value ? m.color : 'transparent' }}
                    onClick={() => setJournalForm({ ...journalForm, mood: m.value })}>{m.label}</button>
                ))}
              </div>
              <label style={s.label}>Notes / Observations</label>
              <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={journalForm.notes} onChange={e => setJournalForm({ ...journalForm, notes: e.target.value })} placeholder="How was the day? Any concerns?" />
              <label style={s.label}>Physical Activity</label>
              <input style={s.input} value={journalForm.activity} onChange={e => setJournalForm({ ...journalForm, activity: e.target.value })} placeholder="e.g. Short walk, yoga" />
              <label style={s.label}>Appetite</label>
              <select style={s.input} value={journalForm.appetite} onChange={e => setJournalForm({ ...journalForm, appetite: e.target.value })}>
                <option value="">Select...</option>
                <option>Excellent</option>
                <option>Good</option>
                <option>Fair</option>
                <option>Poor</option>
                <option>Refused to eat</option>
              </select>
              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeJournalModal}>Cancel</button>
                <button type="submit" style={s.saveBtn}>{editJournal ? 'Update' : 'Save Entry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== RESULTS MODAL ===== */}
      {showResultsModal && (
        <div style={s.modal}>
          <div style={{ ...s.modalCard, maxWidth: 420 }} className="modal-inner">
            <div style={s.modalTitle}>Appointment Results</div>
            <div style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 14 }}>{showResultsModal.title} — {formatDate(showResultsModal.date)}</div>
            <label style={s.label}>Results / Notes</label>
            <textarea style={{ ...s.input, minHeight: 100, resize: 'vertical' }}
              value={resultsText} onChange={e => setResultsText(e.target.value)}
              placeholder="e.g. Blood pressure normal, follow-up in 3 months..." />
            <div style={s.row}>
              <button style={s.cancelBtn} onClick={() => setShowResultsModal(null)}>Cancel</button>
              <button style={s.saveBtn} onClick={saveResults}>Save Results</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EMAIL MODAL ===== */}
      {showEmailModal && (
        <div style={s.modal}>
          <div style={{ ...s.modalCard, maxWidth: 420 }} className="modal-inner">
            <div style={s.modalTitle}>✉️ {T('emailReport')}</div>
            <div style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 14 }}>
              Send a PDF report for <strong>{caregiver?.name}</strong> to any email address.
            </div>
            {emailMsg && (
              <div style={{ background: emailMsg.type === 'success' ? 'var(--success-bg)' : 'var(--error-bg)', color: emailMsg.type === 'success' ? 'var(--success-text)' : 'var(--error-text)', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontWeight: 600 }}>
                {emailMsg.text}
              </div>
            )}
            <label style={s.label}>Send to Email *</label>
            <input style={s.input} type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="recipient@email.com" required />
            <div style={s.row}>
              <button style={s.cancelBtn} onClick={() => setShowEmailModal(false)}>{T('cancel')}</button>
              <button style={{ ...s.saveBtn, background: '#0891b2', opacity: emailSending ? 0.7 : 1 }}
                onClick={sendEmailReport} disabled={emailSending || !emailTo}>
                {emailSending ? 'Sending...' : '✉️ Send Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
